import React, { useState, useEffect, useCallback } from "react";
import { FaMicrophone } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoClose } from "react-icons/io5";
import { HiMiniSpeakerWave } from "react-icons/hi2";
import WhisperVoiceConfig from "./useWhisper";

const VoiceInput = ({
  closeVoiceInput,
  setCurrentVideo,
  idleVideo,
  handleSendMessage,
  mode,
  setMode,
  aiResponse,
}) => {
  const [micPermission, setMicPermission] = useState(null);

  // Auto-send when silence is detected
  const handleSilence = useCallback(
    (text) => {
      if (text && mode === "listen") {
        setMode("thinking");
      }
    },
    [mode, setMode]
  );

  const { transcript, recording, startRecording, stopRecording, liveText } =
    WhisperVoiceConfig({ onSilence: handleSilence, silenceTimeout: 2500 });

  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        setMicPermission(true);
      } catch (error) {
        setMicPermission(false);
      }
    };

    checkMicPermission();
  }, []);

  useEffect(() => {
    if (micPermission === false) {
      alert(
        "Microphone access is blocked. Please enable it in your browser settings."
      );
    }
  }, [micPermission]);

  // Handle mode changes
  useEffect(() => {
    switch (mode) {
      case "silent":
        setCurrentVideo(idleVideo);
        stopRecording();
        transcript.text = null;
        break;
      case "listen":
        setCurrentVideo(idleVideo);
        if (!recording) {
          startRecording();
        }
        break;
      case "thinking":
        setCurrentVideo(idleVideo);
        stopRecording();
        // Safe null check before sending
        if (transcript.text && transcript.text.trim()) {
          handleSendMessage(transcript.text.trim());
        } else {
          // Nothing to send, go back to silent
          setMode("silent");
        }
        break;
      case "talk":
        transcript.text = null;
        break;
      default:
        setCurrentVideo(idleVideo);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleMicrophoneClick = () => {
    setMode((prevMode) => {
      switch (prevMode) {
        case "silent":
          return "listen";
        case "listen":
          // If user taps while listening, force-send what we have
          if (transcript.text && transcript.text.trim()) {
            return "thinking";
          }
          // If nothing said yet, stay listening
          return "listen";
        case "talk":
          return "silent";
        default:
          return "silent";
      }
    });
  };

  const normalizeLanguage = (language) => {
    const normalizedLanguage = (
      language ||
      sessionStorage.getItem("language") ||
      ""
    ).toLowerCase();

    if (normalizedLanguage === "en" || normalizedLanguage === "english") {
      return "English";
    } else if (
      normalizedLanguage === "id" ||
      normalizedLanguage === "indonesia"
    ) {
      return "Indonesia";
    }
    return "English";
  };

  const modeDetails = (language) => {
    const text = {
      English: {
        silent: "Tap to start talking",
        listen: "Listening...",
        thinking: "Processing...",
      },
      Indonesia: {
        silent: "Ketuk untuk mulai berbicara",
        listen: "Mendengarkan...",
        thinking: "Memproses...",
      },
    };

    const currentLanguage = normalizeLanguage(language);
    const currentText = text[currentLanguage] || text.English;

    return {
      silent: {
        text: currentText.silent,
        icon: <FaMicrophone />,
        textClassName: "text-silent",
        className: "microphone__button silent",
      },
      listen: {
        text: currentText.listen,
        icon: <FaMicrophone />,
        textClassName: "text-listen",
        className:
          micPermission === false
            ? "microphone__button not-listen"
            : "microphone__button listen pulse",
      },
      thinking: {
        text: currentText.thinking,
        icon: <AiOutlineLoading3Quarters className="loading-icon spinner" />,
        textClassName: "text-thinking",
        className: "microphone__button thinking",
      },
      talk: {
        text: aiResponse || "",
        icon: <HiMiniSpeakerWave className="heartbeat" />,
        textClassName: "text-talk",
        className: "microphone__button talk",
      },
    };
  };

  const currentMode = modeDetails(sessionStorage.getItem("language"))[mode] || {};

  return (
    <div className="voice-input">
      <div className="container__voice-input">
        {/* Real-time transcript display */}
        {mode === "listen" && liveText ? (
          <p className="voice-input__transcript">{liveText}</p>
        ) : mode === "listen" ? (
          <p className="voice-input__transcript voice-input__transcript--waiting">
            {normalizeLanguage(sessionStorage.getItem("language")) === "Indonesia"
              ? "Mulai berbicara..."
              : "Start speaking..."}
          </p>
        ) : null}
        <p className={currentMode.textClassName}>{currentMode.text}</p>
        <div style={{ position: "relative", width: "100%", height: "100px", display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "25px" }}>
          <div className={currentMode.className} onClick={handleMicrophoneClick}>
            {currentMode.icon}
          </div>
          <div className="microphone__close-button" onClick={closeVoiceInput}>
            <IoClose />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceInput;
